import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import {
    BehaviorSubject,
    Observable,
    catchError,
    filter,
    map,
    of,
    switchMap,
    take,
    tap,
    throwError,
} from 'rxjs';
import { StaffCounts, StaffWithStockRequests, StaffWithStockRequestsPageResponse, StockRequests } from './staff.types';
import { environment } from 'src/environments/environment';
import { ApiResponse } from 'src/app/core/utils/api-response';
import { Staff } from 'src/app/core/staff/staff.types';

@Injectable({ providedIn: 'root' })
export class StaffService {
    // Private
    private API_URL = environment.apiUrl;

    // Private
    private _staff: BehaviorSubject<Staff | null> = new BehaviorSubject<Staff | null>(
        null
    );
    private _staffList: BehaviorSubject<Staff[] | null> = new BehaviorSubject<Staff[] | null>(
        null
    );

    private _staffWithStockRequests: BehaviorSubject<StaffWithStockRequests[] | null> = new BehaviorSubject<StaffWithStockRequests[] | null>(null);

    private _staffCounter: BehaviorSubject<StaffCounts | null> = new BehaviorSubject<StaffCounts | null>(
        null
    );

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient) { }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for staff
     */
    get staff$(): Observable<Staff | null> {
        return this._staff.asObservable();
    }

    /**
     * Getter for staff list
     */
    get staffList$(): Observable<Staff[] | null> {
        return this._staffList.asObservable();
    }

    /**
     * Getter for staff Counter
     */
    get staffCounter$(): Observable<StaffCounts | null> {
        return this._staffCounter.asObservable();
    }

    /**
     * Getter for staff with stock requests
     */
    get staffWithStockRequests$(): Observable<StaffWithStockRequests[] | null> {
        return this._staffWithStockRequests.asObservable();
    }


    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------



    /**
     * Search contacts with given query
     *
     * @param query
     */
    searchStaffs(query: string): Observable<Staff[]> {
        return this._httpClient
            .get<Staff[]>('api/apps/staffs/search', {
                params: { query },
            })
            .pipe(
                tap((staffs) => {
                    this._staffList.next(staffs);
                })
            );
    }

    /**
     * Get contact by id
     */
        /**
     * Get staff member by code
     * 
     * @param code - Staff code to retrieve
     * @returns Observable<ApiResponse<Staff>> - Staff information
     */
    getStaffByCode(code: string): Observable<ApiResponse<Staff>> {
      return this._httpClient
        .get<ApiResponse<Staff>>(`${this.API_URL}/api/v1/staff/${code}`)
        .pipe(
          tap((response) => {
            if (response.success && response.data) {
              // Update the selected staff in the store
              this._staff.next(response.data);
            }
          }),
          catchError((error) => {
            // Log error for debugging
            console.error(`Failed to fetch staff with code ${code}:`, error);
    
            // Return standardized error response
            const errorResponse: ApiResponse<Staff> = error?.error || {
              success: 0,
              message: `Could not find staff with code of ${code}!`,
              data: null,
              timestamp: new Date()
            };
    
            return throwError(() => errorResponse);
          })
        );
    }

    /**
     * Get staff members by school code
     *
     * @param schoolCode - The unique identifier of the school
     * @returns Observable<ApiResponse<Staff[]>> - List of staff members for the school
     */
    getStaffsBySchool(schoolCode: string): Observable<ApiResponse<Staff[]>> {
        return this._httpClient
            .get<ApiResponse<Staff[]>>(`${this.API_URL}/api/v1/staff/school/${schoolCode}`)
            .pipe(
                tap((response) => {
                    if (response.success && response.data) {
                        // Update the staff list in the store
                        this._staffList.next(response.data);
                    }
                }),
                catchError((error) => {
                    // Log error for debugging
                    console.error('Failed to fetch staff members:', error);

                    // Return standardized error response
                    const errorResponse: ApiResponse<Staff[]> = error?.error || {
                        success: 0,
                        message: 'Failed to fetch staff members',
                        data: null,
                        timestamp: new Date()
                    };

                    return throwError(() => errorResponse);
                })
            );
    }

    getStaffsWithStokeRequestsBySchool(schoolCode: string): Observable<ApiResponse<StaffWithStockRequests[]>> {
        return this._httpClient
            .get<ApiResponse<StaffWithStockRequests[]>>(`${this.API_URL}/inventory/staff/school/${schoolCode}`)
            .pipe(
                tap((response) => {
                    if (response.success && response.data) {
                        // Update the staff list in the store
                        this._staffWithStockRequests.next(response.data);
                    }
                }),
                catchError((error) => {
                    // Log error for debugging
                    console.error('Failed to fetch staff members:', error);

                    // Return standardized error response
                    const errorResponse: ApiResponse<StaffWithStockRequests[]> = error?.error || {
                        success: 0,
                        message: 'Failed to fetch staff members',
                        data: null,
                        timestamp: new Date()
                    };

                    return throwError(() => errorResponse);
                })
            );
    }

    getStaffsPaged(
            schoolCode: string,
            page: number,
            size: number,
            search: string = '',
            sortField: string = '',
            sortOrder: number = 1,
            filters: { [key: string]: any } = {}
        ): Observable<ApiResponse<StaffWithStockRequestsPageResponse>> {
            let url = `${this.API_URL}/inventory/staff/school/${schoolCode}/paged?page=${page}&size=${size}`;
            if (search) url += `&search=${encodeURIComponent(search)}`;
            if (sortField) url += `&sort=${sortField},${sortOrder === 1 ? 'asc' : 'desc'}`;
    
            // Add filters as query params (array or object)
            Object.keys(filters).forEach(key => {
                const value = filters[key];
                if (Array.isArray(value)) {
                    value.forEach(v => url += `&${key}=${encodeURIComponent(v)}`);
                } else if (value !== undefined && value !== null && value !== '') {
                    url += `&${key}=${encodeURIComponent(value)}`;
                }
            });
    
            return this._httpClient.get<ApiResponse<StaffWithStockRequestsPageResponse>>(url)
            .pipe(
                tap((response) => {
                    if (response.success && response.data && Array.isArray(response.data.staffPage.content)) {
                        this._staffWithStockRequests.next(response.data.staffPage.content);
                        this._staffCounter.next(response.data.staffCounter);
                    }
                }),
                catchError((error) => {
                    // ...error handling...
                    return throwError(() => error);
                })
            );
        }
    

    /**
     * Get staff counts from staffCounter BehaviorSubject
     * @returns Observable<ApiResponse<StaffCounts>>
     */
    getStaffCounts(): Observable<ApiResponse<StaffCounts>> {
        return this._staffCounter.asObservable().pipe(
            take(1),
            map((counts) => {
                if (!counts) {
                    return {
                        success: true,
                        data: {
                            totalUsers: 0,
                            totalStaff: 0,
                            totalAdmins: 0
                        },
                        message: 'No staff data available',
                        timestamp: new Date()
                    };
                }
                return {
                    success: true,
                    data: counts,
                    message: 'Staff counts retrieved successfully',
                    timestamp: new Date()
                };
            })
        );
    }


    /**
     * Create staff
     */
    createStaff(staff: Staff): Observable<ApiResponse<Staff>> {
        return this.staffList$.pipe(
            take(1),
            switchMap((staffs) =>
                this._httpClient
                    .post<ApiResponse<Staff>>(`${this.API_URL}/api/v1/staff`, staff)
                    .pipe(
                        tap((response) => {
                            if (response.success && response.data) {
                                // Update the staff list with the new staff
                                const staffRequest: StockRequests = {
                                    code: '',
                                    requestPending: 0,
                                    requestCompleted: 0,
                                    consumption: 0
                                };
                                const newStaffWithStockRequest: StaffWithStockRequests = {
                                    staff: response.data,
                                    stockRequests: staffRequest
                                };
                                this._staffWithStockRequests.next([newStaffWithStockRequest, ...(this._staffWithStockRequests.value || [])]);
                                this._staffList.next([response.data, ...(staffs || [])]);
                            }
                        }),
                        catchError((error) => {
                            // Log error for debugging
                            console.error('Failed to create staff:', error);

                            // Return standardized error response
                            const errorResponse: ApiResponse<Staff> = error?.error || {
                                success: 0,
                                message: 'Failed to create staff member',
                                data: null,
                                timestamp: new Date()
                            };

                            return throwError(() => errorResponse);
                        })
                    )
            )
        );
    }

    /**
 * Update staff member
 *
 * @param staff - Staff data to update
 * @returns Observable<ApiResponse<Staff>> - Response with updated staff data
 */
    updateStaff(staff: Staff): Observable<ApiResponse<Staff>> {
        return this.staffList$.pipe(
            take(1),
            switchMap((staffs) =>
                this._httpClient
                    .patch<ApiResponse<Staff>>(`${this.API_URL}/api/v1/staff/${staff.code}`, staff)
                    .pipe(
                        tap((response) => {
                            if (response.success && response.data) {
                                // Find index of staff to update
                                const index = staffs?.findIndex(item => item.code === staff.code) ?? -1;

                                // Update staff in local list
                                if (index !== -1 && staffs) {
                                    staffs[index] = response.data;
                                    // Update the staffs list
                                    this._staffList.next(staffs);
                                }

                                // Update selected staff if it's the current one
                                if (this._staff.value?.code === staff.code) {
                                    this._staff.next(response.data);
                                }
                            }
                        }),
                        catchError((error) => {
                            // Log error for debugging
                            console.error('Failed to update staff:', error);

                            // Return standardized error response
                            const errorResponse: ApiResponse<Staff> = error?.error || {
                                success: 0,
                                message: 'Failed to update staff member',
                                data: null,
                                timestamp: new Date()
                            };

                            return throwError(() => errorResponse);
                        })
                    )
            )
        );
    }


    /**
     * Delete the staff
     *
     * @param code
     */
    /**
 * Delete staff member
 *
 * @param code - Staff code to delete
 * @returns Observable<ApiResponse<void>> - Response indicating success/failure
 */
    deleteStaff(code: string): Observable<ApiResponse<void>> {
        return this.staffList$.pipe(
            take(1),
            switchMap((staffs) =>
                this._httpClient
                    .delete<ApiResponse<void>>(`${this.API_URL}/api/v1/staff/${code}`)
                    .pipe(
                        tap((response) => {
                            if (response.success) {
                                // Find the index of the deleted staff
                                const index = staffs?.findIndex(item => item.code === code) ?? -1;

                                // Delete the staff from local list
                                if (index !== -1 && staffs) {
                                    staffs.splice(index, 1);
                                    // Update the staffs list
                                    this._staffList.next(staffs);
                                }

                                 // Remove from staffWithStockRequests as well
                                const staffWithStockRequests = this._staffWithStockRequests.value || [];
                                const filtered = staffWithStockRequests.filter(item => item.staff?.code !== code);
                                this._staffWithStockRequests.next(filtered);

                            }
                        }),
                        catchError((error) => {
                            console.error('Failed to delete staff:', error);
                            const errorResponse: ApiResponse<void> = error?.error || {
                                success: 0,
                                message: 'Failed to delete staff member',
                                data: null,
                                timestamp: new Date()
                            };
                            return throwError(() => errorResponse);
                        })
                    )
            )
        );
    }


    /**
     * Get user name by email
     * 
     * @param email - Email address to check
     * @returns Observable<ApiResponse<{firstName: string, lastName: string}>>
     */
    getUserNameByEmail(email: string): Observable<ApiResponse<{firstName: string, lastName: string}>> {
        return this._httpClient
            .get<ApiResponse<{firstName: string, lastName: string}>>(
                `${this.API_URL}/api/v1/staff/user/name?email=${encodeURIComponent(email)}`
            )
            .pipe(
                catchError((error) => {
                    console.error('Failed to fetch user by email:', error);
                    const errorResponse: ApiResponse<{firstName: string, lastName: string}> = error?.error || {
                        success: false,
                        message: 'User not found',
                        data: null,
                        timestamp: new Date()
                    };
                    return throwError(() => errorResponse);
                })
            );
    }


}
